import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Select, Button, Table, message, Spin, DatePicker, InputNumber, Checkbox, Input, Form, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { ReloadOutlined, PlusOutlined, SaveOutlined, EditOutlined, CloseOutlined, UserAddOutlined } from '@ant-design/icons';
import * as subjectService from '../../services/subjectService';
import { buildAPIURL } from '../../config/apiConfig';

// API base
const apiBase = buildAPIURL('');

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('quanly_truonghoc_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

interface Course {
  KhoaHocID: string;
  TenKhoaHoc: string;
}

interface Subject {
  SubjectId?: string;
  SubjectID?: string;
  MonHocID?: string;
  TenMonHoc?: string;
  TenMH?: string;
  TongSoBuoi?: number; // Tổng số buổi học
  IsLocked?: boolean; // Khóa điểm danh môn học
}

interface PracticeGroup {
  MaNhomThucHanh?: string;
  TenNhomThucHanh?: string;
  LopHocPhanID?: string;
  MaLHP?: string;
  TenLHP?: string;
  TenLopHocPhan?: string;
  TenNhom?: string;
}

interface Student {
  MaSV?: string;
  MaHS?: string;
  HoTen: string;
  NgaySinh?: string | Date;
  LopHocID?: string;
  LopSinhHoat?: string;
  LopSH?: string;
  Vang?: boolean;
  Muon?: number;
  BoTiet?: boolean;
  DiemTX?: number;
  KhongDat?: boolean;
  LyDo?: string;
  CoHoaChat?: boolean;
}

interface LopHoc {
  LopHocID: string;
  TenLop: string;
}

const AttendancePracticePage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [practiceGroups, setPracticeGroups] = useState<PracticeGroup[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedPracticeGroup, setSelectedPracticeGroup] = useState<string | null>(null);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [session, setSession] = useState<string>('Sáng');
  const [soTietLT, setSoTietLT] = useState<number>(0);
  const [soTietTH, setSoTietTH] = useState<number>(0);
  const [suDungMayChieu, setSuDungMayChieu] = useState<boolean>(false);
  const [coHoaChat, setCoHoaChat] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(true);
  const [attendanceData, setAttendanceData] = useState<Student[]>([]);
  const [tenBaiGiang, setTenBaiGiang] = useState<string>('');
  const [buoiSo, setBuoiSo] = useState<number | null>(null);
  const [buoiGiang, setBuoiGiang] = useState<string>('');
  const [ngayGiang, setNgayGiang] = useState<Dayjs>(dayjs());
  const [khoaHocId, setKhoaHocId] = useState<string>('');
  const [monHocId, setMonHocId] = useState<string>('');
  const [lopHocPhanId, setLopHocPhanId] = useState<string>('');
  const [lopHocs, setLopHocs] = useState<LopHoc[]>([]);
  const [isNewSession, setIsNewSession] = useState<boolean>(false);
  const [siSo, setSiSo] = useState<number>(0);


  // Modal states for TongSoBuoi
  const [tongSoBuoiModalVisible, setTongSoBuoiModalVisible] = useState<boolean>(false);
  const [selectedSubjectForTongSoBuoi, setSelectedSubjectForTongSoBuoi] = useState<Subject | null>(null);
  const [tongSoBuoiForm] = Form.useForm();

  const sessionOptions = [
    { label: 'Sáng', value: 'Sáng' },
    { label: 'Chiều', value: 'Chiều' },
    { label: 'Tối', value: 'Tối' }
  ];

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/courses`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      console.log('[DEBUG] API /courses response:', data);

      const coursesArray = data.success && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
      console.log('[DEBUG] Courses array to set:', coursesArray);
      setCourses(coursesArray);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch /courses:', err);
      setCourses([]);
    }
    setLoading(false);
  };

  const fetchSubjects = async (courseId: string) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/subjects/by-khoahoc/${encodeURIComponent(courseId)}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      console.log('[DEBUG] API /subjects response:', data);

      const subjectsArray = data.success && Array.isArray(data.data) ? data.data :
        (data.data && Array.isArray(data.data) ? data.data :
          (Array.isArray(data) ? data : []));
      console.log('[DEBUG] Subjects array to set:', subjectsArray);
      setSubjects(subjectsArray);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch /subjects:', err);
      setSubjects([]);
    }
    setLoading(false);
  };

  const fetchPracticeGroups = async (courseId: string, subjectId: string) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/nhom-thuc-hanh/by-monhoc/${encodeURIComponent(subjectId)}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      console.log('[DEBUG] API /nhom-thuc-hanh response:', data);

      const practiceGroupsArray = data.success && Array.isArray(data.data) ? data.data :
        (data.data && Array.isArray(data.data) ? data.data :
          (Array.isArray(data) ? data : []));
      console.log('[DEBUG] Practice Groups array to set:', practiceGroupsArray);
      setPracticeGroups(practiceGroupsArray);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch /nhom-thuc-hanh:', err);
      setPracticeGroups([]);
    }
    setLoading(false);
  };

  const fetchStudents = async (practiceGroupId: string) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/nhom-thuc-hanh-sinhvien/${encodeURIComponent(practiceGroupId)}/sinhvien/full`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      console.log('[DEBUG] API /nhom-thuc-hanh_sinhvien response:', data);

      const studentArray = data.success && Array.isArray(data.data) ? data.data :
        (data.data && Array.isArray(data.data) ? data.data :
          (Array.isArray(data) ? data : []));
      console.log('[DEBUG] Students array to set:', studentArray);

      // Sắp xếp sinh viên theo Mã SV tăng dần
      const sortedStudentArray = studentArray.sort((a: Student, b: Student) => {
        const maA = (a.MaHS || a.MaSV || '').toString();
        const maB = (b.MaHS || b.MaSV || '').toString();
        return maA.localeCompare(maB);
      });

      setStudents(sortedStudentArray);
      setAttendanceData(sortedStudentArray.map((sv: Student) => ({
        ...sv,
        Vang: false,
        Muon: 0,
        BoTiet: false,
        DiemTX: 0,
        KhongDat: false,
        LyDo: '',
        CoHoaChat: false,
      })));
      // Sĩ số = tổng số sinh viên của nhóm thực hành
      setSiSo(sortedStudentArray.length);
      console.log('[DEBUG] setSiSo (tổng sinh viên nhóm thực hành):', sortedStudentArray.length);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch /nhom-thuc-hanh_sinhvien:', err);
      setStudents([]);
      setAttendanceData([]);
    }
    setLoading(false);
  };

  const fetchAttendance = async () => {
    if (!selectedPracticeGroup || !date || !session) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/attendance-practice?practiceGroupId=${selectedPracticeGroup}&date=${date.format('YYYY-MM-DD')}&session=${session}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();

      console.log('[DEBUG][fetchAttendance] data:', data);
      console.log('[DEBUG] data.students:', data.students, 'typeof:', typeof data.students, 'isArray:', Array.isArray(data.students));

      if (data && Array.isArray(data.students)) {
        setSoTietLT(data.SoTietLT || 0);
        setSoTietTH(data.SoTietTH || 0);
        setSuDungMayChieu(data.SuDungMayChieu || false);
        setCoHoaChat(!!data.CoHoaChat);
        setAttendanceData(data.students);
        setTenBaiGiang(data.TenBaiGiang || '');
        setBuoiSo(data.BuoiSo !== undefined ? Number(data.BuoiSo) : null);
        setBuoiGiang(data.BuoiGiang || '');
        setNgayGiang(data.NgayGiang ? dayjs(data.NgayGiang) : dayjs());
        setKhoaHocId(data.KhoaHocID || '');
        setMonHocId(data.MonHocID || '');
        setLopHocPhanId(data.LopHocPhanID || '');
        setSiSo(data.students.length);
        setEditMode(false);
        setIsNewSession(false);
      } else {
        setEditMode(true);
        setIsNewSession(true);
        setSoTietLT(0);
        setSoTietTH(0);
        setSuDungMayChieu(false);
        setCoHoaChat(false);
        setAttendanceData([]);
        setTenBaiGiang('');
        setBuoiSo(null);
        setBuoiGiang('');
        setNgayGiang(dayjs());
        setKhoaHocId('');
        setMonHocId('');
        setLopHocPhanId('');
        setSiSo(0);
      }
    } catch {
      setEditMode(true);
      setIsNewSession(true);
      setSoTietLT(0);
      setSoTietTH(0);
      setSuDungMayChieu(false);
      setCoHoaChat(false);
      setAttendanceData([]);
      setTenBaiGiang('');
      setBuoiSo(null);
      setBuoiGiang('');
      setNgayGiang(dayjs());
      setKhoaHocId('');
      setMonHocId('');
      setLopHocPhanId('');
      setSiSo(0);
    }
    setLoading(false);
  };

  const saveAttendance = async () => {
    // Bắt buộc Buổi số
    if (!buoiSo || buoiSo <= 0) {
      message.error('Vui lòng nhập Buổi số trước khi lưu!');
      return;
    }
    // Kiểm tra không vượt Tổng số buổi của môn học (nếu có)
    try {
      if (selectedSubject && subjects.length > 0) {
        const currentSubject = subjects.find(s => String(s.SubjectId || s.SubjectID || s.MonHocID) === selectedSubject);
        const maxBuoi = currentSubject?.TongSoBuoi ? Number(currentSubject.TongSoBuoi) : null;
        if (maxBuoi && buoiSo > maxBuoi) {
          message.error(`Buổi số ${buoiSo} vượt quá Tổng số buổi (${maxBuoi}) của môn học.`);
          return;
        }
      }
    } catch { }
    setLoading(true);
    try {
      // Lấy thông tin giảng viên từ localStorage hoặc sessionStorage
      let currentUser = localStorage.getItem('username') || sessionStorage.getItem('username') ||
        localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser') || '';

      // Kiểm tra trong quanly_truonghoc_user
      if (!currentUser) {
        try {
          const userDataStr = localStorage.getItem('quanly_truonghoc_user') || sessionStorage.getItem('quanly_truonghoc_user');
          if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            currentUser = userData.Username || userData.username || '';
          }
        } catch (e) {
          console.error('[DEBUG] Error parsing user data:', e);
        }
      }

      // Chuyển đổi buổi giảng thành ký tự đầu tiên
      const buoiGiangCode = session.charAt(0).toUpperCase(); // S, C, T

      // Map lại students đầy đủ trường
      const mappedStudents = attendanceData.map(sv => ({
        MaSV: sv.MaSV || sv.MaHS,
        HoTen: sv.HoTen,
        NgaySinh: sv.NgaySinh,
        LopSinhHoat: sv.LopSinhHoat || sv.LopHocID || sv.LopSH,
        Vang: sv.Vang || false,
        Muon: sv.Muon || 0,
        BoTiet: sv.BoTiet || false,
        DiemTX: sv.DiemTX || 0,
        KhongDat: sv.KhongDat || false,
        LyDo: sv.LyDo || '',
        CoHoaChat: sv.CoHoaChat || false
      }));

      // Log toàn bộ payload
      const payload = {
        practiceGroupId: selectedPracticeGroup,
        date: date.format('YYYY-MM-DD'),
        session,
        buoiSo,
        tenBaiGiang,
        soTietLT,
        soTietTH,
        suDungMayChieu,
        coHoaChat,
        students: mappedStudents,
        khoaHocId: selectedCourse,
        monHocId: selectedSubject,
        lopHocPhanId: selectedPracticeGroup, // Lưu nhóm thực hành vào LopHocPhanID
        ngayGiang: date.format('YYYY-MM-DD'),
        buoiGiang: buoiGiangCode,
        siSo,
        giangVien: currentUser,
        isEditMode: editMode && !isNewSession
      };
      console.log('[saveAttendance] Payload:', payload);

      const res = await fetch(`${apiBase}/attendance-practice`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok && data.success) {
        message.success(data.message || 'Lưu dữ liệu thành công!');
        setEditMode(false);
        setIsNewSession(false);
      } else {
        const errMsg = data.error || data.message || 'Lưu điểm danh thất bại!';
        message.error(errMsg);
      }
    } catch (e) {
      message.error('Lưu điểm danh thất bại!');
    }
    setLoading(false);
  };

  const resetAttendanceForm = () => {
    setSelectedCourse(null);
    setSelectedSubject(null);
    setSelectedPracticeGroup(null);
    setDate(dayjs());
    setSession('Sáng');
    setSoTietLT(0);
    setSoTietTH(0);
    setSuDungMayChieu(false);
    setCoHoaChat(false);
    setAttendanceData([]);
    setTenBaiGiang('');
    setBuoiSo(null);
    setBuoiGiang('');
    setNgayGiang(dayjs());
    setKhoaHocId('');
    setMonHocId('');
    setLopHocPhanId('');
    setEditMode(true);
    setIsNewSession(true);
    setSiSo(0);
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await fetch(`${apiBase}/classes`, {
          headers: getAuthHeaders()
        });
        const data = await res.json();
        setLopHocs(Array.isArray(data) ? data : []);
        console.log('[DEBUG] API /classes:', data);
      } catch (err) {
        console.error('[DEBUG] Lỗi fetch /classes:', err);
        setLopHocs([]);
      }
    };
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedCourse) fetchSubjects(selectedCourse);
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedCourse && selectedSubject) fetchPracticeGroups(selectedCourse, selectedSubject);
  }, [selectedCourse, selectedSubject]);

  // Check TongSoBuoi and IsLocked when subject is selected
  useEffect(() => {
    if (selectedSubject && subjects.length > 0) {
      const currentSubject = subjects.find(s =>
        String(s.SubjectId || s.SubjectID || s.MonHocID) === selectedSubject
      );

      if (currentSubject) {
        // Kiểm tra môn học có bị khóa không
        if (currentSubject.IsLocked) {
          message.warning('Môn học này đã bị khóa điểm danh. Không thể thực hiện điểm danh.');
          setSelectedSubject(null);
          return;
        }

        // Kiểm tra TongSoBuoi
        if (!currentSubject.TongSoBuoi || currentSubject.TongSoBuoi === 0) {
          // Hiển thị modal yêu cầu nhập số buổi
          setSelectedSubjectForTongSoBuoi(currentSubject);
          setTongSoBuoiModalVisible(true);
        }
      }
    }
  }, [selectedSubject, subjects]);

  useEffect(() => {
    if (isNewSession) return;
    if (selectedPracticeGroup && date && session) fetchAttendance();
  }, [selectedPracticeGroup, date, session, isNewSession]);

  useEffect(() => {
    resetAttendanceForm();
  }, []);

  const handleGetStudents = (): void => {
    if (!selectedPracticeGroup) {
      message.warning('Vui lòng chọn nhóm thực hành!');
      return;
    }
    fetchStudents(selectedPracticeGroup);
  };

  const handleAttendanceChange = (index: number, field: string, value: any) => {
    setAttendanceData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleEdit = () => setEditMode(true);

  const handleClose = () => {
    resetAttendanceForm();
    window.history.back();
  };

  // Handle TongSoBuoi update
  const handleTongSoBuoiSubmit = async () => {
    try {
      const values = await tongSoBuoiForm.validateFields();
      const tongSoBuoi = values.tongSoBuoi;

      if (!selectedSubjectForTongSoBuoi) return;

      const subjectId = selectedSubjectForTongSoBuoi.SubjectId ||
        selectedSubjectForTongSoBuoi.SubjectID ||
        selectedSubjectForTongSoBuoi.MonHocID;

      if (!subjectId) {
        message.error('Không thể xác định ID môn học');
        return;
      }

      // Gọi API cập nhật
      await subjectService.updateSubjectTongSoBuoi(subjectId, tongSoBuoi);

      // Cập nhật local state
      setSubjects(prev => prev.map(subject =>
        (subject.SubjectId || subject.SubjectID || subject.MonHocID) === subjectId
          ? { ...subject, TongSoBuoi: tongSoBuoi }
          : subject
      ));

      message.success('Cập nhật tổng số buổi học thành công!');
      setTongSoBuoiModalVisible(false);
      tongSoBuoiForm.resetFields();
      setSelectedSubjectForTongSoBuoi(null);

    } catch (error) {
      console.error('Error updating TongSoBuoi:', error);
      message.error('Không thể cập nhật tổng số buổi học');
    }
  };

  const handleTongSoBuoiCancel = () => {
    setTongSoBuoiModalVisible(false);
    tongSoBuoiForm.resetFields();
    setSelectedSubjectForTongSoBuoi(null);
    // Reset selected subject to prevent continuous loop
    setSelectedSubject(null);
  };

  const normalizeLopHocID = (id: string | undefined) => (id || '').toString().trim();

  // Sửa map lớp sinh hoạt
  const lopHocMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(lopHocs)) {
      lopHocs.forEach(lh => {
        map[(lh.LopHocID || '').toString().trim()] = lh.TenLop;
      });
    }
    return map;
  }, [lopHocs]);

  const columns: ColumnsType<Student> = [
    {
      title: 'Mã SV',
      dataIndex: 'MaHS',
      key: 'MaHS',
      sorter: (a: Student, b: Student) => (a.MaHS || a.MaSV || '').localeCompare(b.MaHS || b.MaSV || ''),
      sortDirections: ['ascend', 'descend'],
      filteredValue: null
    },
    {
      title: 'Họ và tên',
      dataIndex: 'HoTen',
      key: 'HoTen',
      sorter: (a: Student, b: Student) => (a.HoTen || '').localeCompare(b.HoTen || ''),
      sortDirections: ['ascend', 'descend'],
      filters: Array.isArray(attendanceData) ? Array.from(new Set(attendanceData.map(sv => sv.HoTen).filter((name): name is string => Boolean(name)))).map(name => ({ text: name, value: name })) : [],
      onFilter: (value, record: Student) => record.HoTen === value
    },
    {
      title: 'Ngày sinh',
      dataIndex: 'NgaySinh',
      key: 'NgaySinh',
      sorter: (a: Student, b: Student) => {
        const dateA = a.NgaySinh ? new Date(a.NgaySinh).getTime() : 0;
        const dateB = b.NgaySinh ? new Date(b.NgaySinh).getTime() : 0;
        return dateA - dateB;
      },
      sortDirections: ['ascend', 'descend'],
      render: (text: string | Date) => text ? dayjs(text).format('DD/MM/YYYY') : ''
    },
    {
      title: 'Vắng',
      dataIndex: 'Vang',
      key: 'Vang',
      render: (_: any, __: Student, idx: number) => (
        <Checkbox
          checked={attendanceData[idx]?.Vang || false}
          disabled={!editMode}
          onChange={e => handleAttendanceChange(idx, 'Vang', e.target.checked)}
        />
      )
    },
    {
      title: 'Muộn',
      dataIndex: 'Muon',
      key: 'Muon',
      render: (_: any, __: Student, idx: number) => (
        <InputNumber
          min={0}
          value={attendanceData[idx]?.Muon || 0}
          disabled={!editMode}
          onChange={v => handleAttendanceChange(idx, 'Muon', v || 0)}
          style={{ width: 60 }}
        />
      )
    },
    {
      title: 'Bỏ tiết',
      dataIndex: 'BoTiet',
      key: 'BoTiet',
      render: (_: any, __: Student, idx: number) => (
        <Checkbox
          checked={attendanceData[idx]?.BoTiet || false}
          disabled={!editMode}
          onChange={e => handleAttendanceChange(idx, 'BoTiet', e.target.checked)}
        />
      )
    },
    {
      title: 'Điểm TX',
      dataIndex: 'DiemTX',
      key: 'DiemTX',
      render: (_: any, __: Student, idx: number) => (
        <InputNumber
          min={0}
          max={10}
          step={0.1}
          value={attendanceData[idx]?.DiemTX || 0}
          disabled={!editMode}
          onChange={v => handleAttendanceChange(idx, 'DiemTX', v || 0)}
          style={{ width: 60 }}
        />
      )
    },
    {
      title: 'Không đạt',
      dataIndex: 'KhongDat',
      key: 'KhongDat',
      render: (_: any, __: Student, idx: number) => (
        <Checkbox
          checked={attendanceData[idx]?.KhongDat || false}
          disabled={!editMode}
          onChange={e => handleAttendanceChange(idx, 'KhongDat', e.target.checked)}
        />
      )
    },
    {
      title: 'Lý do',
      dataIndex: 'LyDo',
      key: 'LyDo',
      render: (_: any, __: Student, idx: number) => (
        <Input
          value={attendanceData[idx]?.LyDo || ''}
          disabled={!editMode}
          onChange={e => handleAttendanceChange(idx, 'LyDo', e.target.value)}
          style={{ width: 120 }}
        />
      )
    },
    {
      title: 'Lớp sinh hoạt',
      dataIndex: 'LopHocID',
      key: 'LopHocID',
      sorter: (a: Student, b: Student) => {
        const tenA = lopHocMap[normalizeLopHocID(a.LopHocID)] || '';
        const tenB = lopHocMap[normalizeLopHocID(b.LopHocID)] || '';
        return tenA.localeCompare(tenB);
      },
      filters: Array.isArray(attendanceData) ? Array.from(new Set(
        attendanceData
          .map(sv => lopHocMap[normalizeLopHocID(sv.LopHocID)] || sv.LopHocID)
          .filter((name): name is string => Boolean(name) && typeof name === 'string')
      )).map(name => ({ text: name, value: name })) : [],
      onFilter: (value, record: Student) => (lopHocMap[normalizeLopHocID(record.LopHocID)] || record.LopHocID) === value,
      render: (lopHocId: string) => lopHocMap[normalizeLopHocID(lopHocId)] || lopHocId
    },
  ];

  return (
    <div style={{ padding: 24, background: '#f6f7fb', minHeight: '100vh' }}>
      <h2 style={{ fontWeight: 700, marginBottom: 16 }}>Điểm danh thực hành</h2>
      <Button type="primary" icon={<ReloadOutlined />} onClick={resetAttendanceForm} style={{ marginBottom: 12 }}>
        Tạo mới phiếu điểm danh
      </Button>

      <Card style={{ marginBottom: 24 }}>
        {/* Dòng 1: Khóa học, Môn học, Nhóm thực hành */}
        <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
          <Col span={8}>
            <Form.Item label="Khóa học" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn khóa học"
                value={selectedCourse}
                onChange={setSelectedCourse}
                options={Array.isArray(courses) ? courses.map(c => ({ value: c.KhoaHocID, label: `${c.KhoaHocID} - ${c.TenKhoaHoc}` })) : []}
                allowClear
                disabled={!editMode}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Môn học" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn môn học"
                value={selectedSubject}
                onChange={setSelectedSubject}
                options={Array.isArray(subjects) ? subjects.map(s => ({
                  value: String(s.SubjectId || s.SubjectID || s.MonHocID),
                  label: `${s.SubjectId || s.SubjectID || s.MonHocID} - ${s.TenMonHoc || s.TenMH}`
                })) : []}
                allowClear
                disabled={!selectedCourse || !editMode}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Nhóm thực hành" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn nhóm thực hành"
                value={selectedPracticeGroup}
                onChange={setSelectedPracticeGroup}
                options={Array.isArray(practiceGroups) ? practiceGroups.map((pg: any) => ({
                  value: pg.MaNhomThucHanh || pg.PracticeGroupID,
                  label: `${pg.MaNhomThucHanh || pg.PracticeGroupID} - ${pg.TenNhomThucHanh || pg.TenNhom || pg.GroupName}`
                })) : []}
                allowClear
                disabled={!selectedSubject || !editMode}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Dòng 2: Ngày giảng, Buổi giảng, Buổi số, Số tiết LT, Số tiết TH, Sử dụng máy chiếu + Sĩ số */}
        <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
          <Col span={4}>
            <Form.Item label="Ngày giảng" style={{ marginBottom: 0 }}>
              <DatePicker
                style={{ width: '100%' }}
                value={date}
                onChange={(d) => setDate(d || dayjs())}
                disabled={!editMode}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item label="Buổi giảng" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '100%' }}
                value={session}
                onChange={setSession}
                options={sessionOptions}
                disabled={!editMode}
              />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item label="Buổi số" style={{ marginBottom: 0 }}>
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                value={buoiSo}
                onChange={(v) => { setBuoiSo(v as number | null); }}
                placeholder="Buổi số"
                disabled={!editMode}
              />

            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item label="Số tiết LT" style={{ marginBottom: 0 }}>
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="LT"
                value={soTietLT}
                onChange={(v) => setSoTietLT(v || 0)}
                disabled={!editMode}
              />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item label="Số tiết TH" style={{ marginBottom: 0 }}>
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="TH"
                value={soTietTH}
                onChange={(v) => setSoTietTH(v || 0)}
                disabled={!editMode}
              />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item label="Có hóa chất" style={{ marginBottom: 0 }}>
              <Checkbox
                checked={coHoaChat}
                onChange={e => setCoHoaChat(e.target.checked)}
                disabled={!editMode}
                style={{ marginTop: 8 }}
              >
                Có
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={2}>
            <Form.Item label="Sĩ số" style={{ marginBottom: 0 }}>
              <Input value={siSo.toString()} disabled style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        {/* Dòng 3: Tên bài giảng + Sử dụng máy chiếu */}
        <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
          <Col span={20}>
            <Form.Item label="Tên bài giảng" style={{ marginBottom: 0 }}>
              <Input
                style={{ width: '100%' }}
                value={tenBaiGiang}
                onChange={e => setTenBaiGiang(e.target.value)}
                placeholder="Nhập tên bài giảng"
                disabled={!editMode}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Máy chiếu" style={{ marginBottom: 0 }}>
              <Checkbox
                checked={suDungMayChieu}
                onChange={e => setSuDungMayChieu(e.target.checked)}
                disabled={!editMode}
                style={{ marginTop: 8 }}
              >
                Sử dụng máy chiếu
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {/* Nút thao tác */}
        <Row gutter={16}>
          <Col span={6}>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleGetStudents}
              loading={loading}
              style={{ width: '100%' }}
              disabled={!editMode}
            >
              Lấy danh sách sinh viên
            </Button>
          </Col>
          <Col span={6}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={saveAttendance}
              loading={loading}
              style={{ width: '100%' }}
              disabled={!editMode || attendanceData.length === 0}
            >
              Lưu
            </Button>
          </Col>
          <Col span={6}>
            <Button
              icon={<EditOutlined />}
              onClick={handleEdit}
              style={{ width: '100%' }}
              disabled={editMode || attendanceData.length === 0}
            >
              Sửa
            </Button>
          </Col>
          <Col span={6}>
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={handleClose}
              style={{ width: '100%' }}
            >
              Đóng
            </Button>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={attendanceData}
          rowKey={(record: Student) => record.MaSV || record.MaHS || Math.random().toString()}
          pagination={false}
          bordered
          scroll={{ x: 'max-content' }}
        />
      </Spin>

      {/* Modal nhập tổng số buổi học */}
      <Modal
        title="Nhập tổng số buổi học"
        open={tongSoBuoiModalVisible}
        onOk={handleTongSoBuoiSubmit}
        onCancel={handleTongSoBuoiCancel}
        okText="Xác nhận"
        cancelText="Bỏ qua"
        maskClosable={false}
        closable={false}
      >
        <div style={{ marginBottom: 16 }}>
          <p><strong>Môn học:</strong> {selectedSubjectForTongSoBuoi?.TenMonHoc || selectedSubjectForTongSoBuoi?.TenMH}</p>
          <p><strong>Mã môn học:</strong> {selectedSubjectForTongSoBuoi?.SubjectId || selectedSubjectForTongSoBuoi?.SubjectID || selectedSubjectForTongSoBuoi?.MonHocID}</p>
          <p style={{ color: '#ff4d4f' }}>
            Môn học này chưa có thông tin tổng số buổi học. Vui lòng nhập để tiếp tục điểm danh.
          </p>
        </div>

        <Form form={tongSoBuoiForm} layout="vertical">
          <Form.Item
            name="tongSoBuoi"
            label="Tổng số buổi học"
            rules={[
              { required: true, message: 'Vui lòng nhập tổng số buổi học' },
              { type: 'number', min: 1, max: 100, message: 'Số buổi phải từ 1 đến 100' }
            ]}
          >
            <InputNumber
              min={1}
              max={100}
              placeholder="Nhập tổng số buổi học (1-100)"
              style={{ width: '100%' }}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AttendancePracticePage; 